import { AutocompleteOptionsWithMetadata, BaseItem } from "@algolia/autocomplete-core";
import { NotificationImpression } from "@jmc/core/src/components/NotificationImpression/NotificationImpression";
import { siteSearchTracking } from "@jmc/core/src/components/Tracking/utils/siteSearchTracking";
import { Button } from "@jmc/solid-design-system/src/components/atoms/Button/Button";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { VoiceIcon } from "@jmc/solid-design-system/src/components/atoms/VoiceIcon/VoiceIcon";
import { Dialog } from "@jmc/solid-design-system/src/components/molecules/Dialog/Dialog";
import Html from "@jmc/solid-design-system/src/components/molecules/Html/Html";
import { Modal } from "@jmc/solid-design-system/src/components/molecules/Modal/Modal";
import useJnjBranding from "@jmc/utils/hooks/useJnjBranding";
import classnames from "classnames";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { acceptVoiceSearchDisclaimer, isVoiceSearchDisclaimerAccepted } from "../voiceSearchApi";
import { VoiceSearchDisclaimer } from "../voiceSearchPlugin";
import style from "./style.module.scss";

interface Props extends AutocompleteOptionsWithMetadata<BaseItem> {
    voiceSearch?: {
        isBrowserSupported?: () => boolean;
        start?: () => void;
        stop?: () => void;
    };
    disclaimer: VoiceSearchDisclaimer;
}

export const VoiceButton = (props: Props): JSX.Element => {
    const { t } = useTranslation();
    const { voiceSearch, disclaimer } = props;
    const [listening, setListening] = useState(false);
    const [disclaimerOpen, setDisclaimerOpen] = useState(false);
    const { jnjFullBranded } = useJnjBranding();

    useEffect(() => {
        const targetNode = document.querySelector(".aa-VoiceSearch");
        const config = { attributes: true, childList: false, subtree: false };

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                const { target } = mutation;
                if (mutation.attributeName === "class") {
                    const active = target?.classList?.contains("active");
                    setListening(active);
                }
            });
        });

        observer.observe(targetNode, config);
        window.addEventListener("keydown", handleKeyPressClose);

        return (): void => {
            observer.disconnect();
            window.removeEventListener("keydown", handleKeyPressClose);
        };
    }, []);

    const handleVoiceSearch = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): Promise<void> => {
        const disclaimerAccepted = await isVoiceSearchDisclaimerAccepted();

        if (listening) {
            voiceSearch?.stop();
        } else {
            if (!disclaimerAccepted) {
                setDisclaimerOpen(true);
                return;
            }
            siteSearchTracking("microphone");
            voiceSearch?.start();
        }
        event && event.preventDefault();
    };

    const handleKeyPressClose = (event: KeyboardEvent) => {
        if (event.key === "Enter" && disclaimerOpen) {
            event.preventDefault();
            event.stopPropagation();
            handleDisclaimerClose();
        }
    };

    const handleDisclaimerClose = (): void => {
        acceptVoiceSearchDisclaimer(true);
        setDisclaimerOpen(false);
        handleVoiceSearch(null);
    };

    return (
        <>
            <button
                className={classnames("aa-VoiceSearchButton", style.voiceButton)}
                title={t("Voice search", { ns: "search" })}
                onClick={(event) => {
                    handleVoiceSearch(event);
                }}
                data-test-id={`search-box-voice-button`}
                // type button prevents button clicked when submitted
                type="button"
            >
                <VoiceIcon listening={listening} />
            </button>

            {jnjFullBranded ? (
                <Dialog
                    open={disclaimerOpen}
                    data-test-id="Voice.Search.Disclaimer"
                    onClose={() => {
                        handleDisclaimerClose();
                    }}
                    noSnippet
                >
                    <div className={style.disclaimer}>
                        <Typography variant="h3" color="title">
                            {disclaimer?.title || "Voice search disclaimer title"}
                        </Typography>

                        <Typography>
                            <Html>{disclaimer?.text || "Voice search disclaimer text"}</Html>
                        </Typography>

                        <NotificationImpression
                            trigger="user-action"
                            type="voice search disclaimer"
                            position={1}
                            name=""
                            action={"close"}
                        >
                            <Button onClick={handleDisclaimerClose} className={style.dialogButton}>
                                {t("Close", { ns: "search" })}
                            </Button>
                        </NotificationImpression>
                    </div>
                </Dialog>
            ) : (
                <Modal size="medium" open={disclaimerOpen} onClose={handleDisclaimerClose}>
                    <Modal.Title align={undefined} title={disclaimer?.title || "Voice search disclaimer title"} />
                    <Modal.Content align={jnjFullBranded ? undefined : "center"}>
                        <Typography>
                            <Html>{disclaimer?.text || "Voice search disclaimer text"}</Html>
                        </Typography>
                    </Modal.Content>
                    <Modal.Actions align={jnjFullBranded ? undefined : "center"}>
                        <Button onClick={handleDisclaimerClose} className={style.dialogButton}>
                            {t("Close", { ns: "search" })}
                        </Button>
                    </Modal.Actions>
                </Modal>
            )}
        </>
    );
};
